import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { 
  X, DollarSign, ShoppingCart, MapPin, Calendar, 
  Plus, AlertCircle, Loader2, CheckCircle2, Trash2, Receipt
} from 'lucide-react';
import { salesApi, CreateSaleRequest } from '@/api/salesApi';
import { productsApi } from '@/api/productsApi';
import { locationsApi } from '@/api/locationsApi';
import { inventoryApi } from '@/api/inventoryApi';
import { handleApiError } from '@/api/client';
import type { ProductVariant } from '@/types/api';

interface RecordSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface SaleItem {
  id: string;
  productId: string;
  variantId: string;
  quantity: string;
  salePrice: string;
}

interface FormErrors {
  locationId?: string;
  items?: string;
  submit?: string;
}

interface BatchSaleResult {
  success: number;
  failed: number;
  errors: string[];
}

export function RecordSaleModal({ isOpen, onClose, onSuccess }: RecordSaleModalProps) {
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<FormErrors>({});
  const [batchResult, setBatchResult] = useState<BatchSaleResult | null>(null);
  
  const [locationId, setLocationId] = useState('');
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState<SaleItem[]>([{ 
    id: '1', 
    productId: '', 
    variantId: '', 
    quantity: '1', 
    salePrice: '' 
  }]);
  const [notes, setNotes] = useState('');

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.getAll,
  });

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.getAll,
  });

  const { data: inventory } = useQuery({
    queryKey: ['inventory', locationId],
    queryFn: () => locationId ? inventoryApi.getVariantsByLocation(locationId) : Promise.resolve([]),
    enabled: !!locationId,
  });

  // Batch sale mutation - creates multiple sales
  const createSalesMutation = useMutation({
    mutationFn: async (salesData: CreateSaleRequest[]): Promise<BatchSaleResult> => {
      const results: BatchSaleResult = { success: 0, failed: 0, errors: [] };
      
      for (const saleData of salesData) {
        try {
          await salesApi.create(saleData);
          results.success++;
        } catch (error: any) {
          results.failed++;
          const errorMsg = handleApiError(error);
          results.errors.push(errorMsg);
        }
      }
      
      return results;
    },
    onSuccess: (result) => {
      setBatchResult(result);
      
      if (result.success > 0) {
        queryClient.invalidateQueries({ queryKey: ['sales'] });
        queryClient.invalidateQueries({ queryKey: ['inventory'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        
        if (result.failed === 0) {
          // All succeeded
          onSuccess?.();
          setTimeout(() => handleClose(), 1500);
        }
      }
    },
    onError: (error: any) => {
      const message = handleApiError(error);
      setErrors(prev => ({ ...prev, submit: message }));
    },
  });

  const getProductVariants = (productId: string): ProductVariant[] => {
    const product = products?.find(p => p.id === productId);
    return product?.variants || [];
  };

  const getVariantPrice = (variantId: string): number => {
    const product = products?.find(p => p.variants.some(v => v.id === variantId));
    return product?.basePrice || 0;
  };

  const getAvailableQuantity = (variantId: string): number => {
    const invItem = inventory?.find(i => i.variantId === variantId);
    return invItem?.availableQuantity || 0;
  };

  const addItem = () => {
    setItems(prev => [...prev, { 
      id: Date.now().toString(),
      productId: '', 
      variantId: '', 
      quantity: '1', 
      salePrice: '' 
    }]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const updateItem = (id: string, field: keyof SaleItem, value: string) => {
    setItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      
      // Auto-set price when variant selected
      if (field === 'variantId' && value) {
        updated.salePrice = getVariantPrice(value).toFixed(2);
      }
      // Reset variant if product changes
      if (field === 'productId') {
        updated.variantId = '';
        updated.salePrice = '';
      }
      return updated;
    }));
  };

  const calculateTotals = () => {
    let total = 0;
    let commission = 0;
    const location = locations?.find(l => l.id === locationId);
    const commissionRate = location?.commissionRate || 0;

    items.forEach(item => {
      const qty = parseInt(item.quantity) || 0;
      const price = parseFloat(item.salePrice) || 0;
      const itemTotal = qty * price;
      total += itemTotal;
      commission += itemTotal * (commissionRate / 100);
    });

    return { total, commission, retained: total - commission, commissionRate };
  };

  const { total, commission, retained, commissionRate } = calculateTotals();

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!locationId) {
      newErrors.locationId = 'Please select a location';
    }

    // Validate at least one item has variant and quantity
    const validItems = items.filter(i => i.variantId && parseInt(i.quantity) > 0);
    if (validItems.length === 0) {
      newErrors.items = 'Please add at least one valid sale item';
    }

    // Check stock availability
    const stockErrors: string[] = [];
    items.forEach((item, idx) => {
      if (item.variantId) {
        const available = getAvailableQuantity(item.variantId);
        const requested = parseInt(item.quantity) || 0;
        if (requested > available) {
          stockErrors.push(`Item ${idx + 1}: Only ${available} in stock`);
        }
      }
    });
    
    if (stockErrors.length > 0) {
      newErrors.items = stockErrors.join(', ');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setBatchResult(null);
    
    if (!validateForm()) return;

    // Build sales data for each valid item
    const salesData: CreateSaleRequest[] = items
      .filter(item => item.variantId && parseInt(item.quantity) > 0)
      .map(item => ({
        productVariantId: item.variantId,
        locationId: locationId,
        quantity: parseInt(item.quantity),
        salePrice: parseFloat(item.salePrice),
        saleDate: new Date(saleDate).toISOString(),
        notes: notes || undefined,
      }));

    createSalesMutation.mutate(salesData);
  };

  const handleClose = () => {
    setLocationId('');
    setSaleDate(new Date().toISOString().split('T')[0]);
    setItems([{ id: '1', productId: '', variantId: '', quantity: '1', salePrice: '' }]);
    setNotes('');
    setErrors({});
    setBatchResult(null);
    createSalesMutation.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-gray-900/60 dark:bg-charcoal-950/80 backdrop-blur-sm animate-fade-in"
        onClick={!createSalesMutation.isPending ? handleClose : undefined}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-charcoal-900 border border-gray-200 dark:border-charcoal-700 shadow-2xl animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-charcoal-700 bg-gray-50 dark:bg-charcoal-850">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-gray-900 dark:text-warm-cream">
                Record Sale
              </h2>
              <p className="text-sm text-gray-600 dark:text-warm-sand">
                Record sales and update inventory
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={createSalesMutation.isPending}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-xl transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-warm-sand" />
          </button>
        </div>

        {/* Error Alert */}
        {(errors.submit || errors.items) && !batchResult && (
          <div className="mx-6 mt-4 p-4 bg-danger-500/10 border border-danger-500/30 rounded-xl animate-shake">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-danger-400">
                  {errors.submit ? 'Failed to record sale' : 'Validation Error'}
                </p>
                <p className="text-sm text-gray-600 dark:text-warm-sand mt-1">
                  {errors.submit || errors.items}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Batch Result Alert */}
        {batchResult && (
          <div className={`
            mx-6 mt-4 p-4 rounded-xl animate-slide-up
            ${batchResult.failed === 0 
              ? 'bg-success-500/10 border border-success-500/30' 
              : batchResult.success > 0 
                ? 'bg-amber-450/10 border border-amber-450/30'
                : 'bg-danger-500/10 border border-danger-500/30'
            }
          `}>
            <div className="flex items-start gap-3">
              {batchResult.failed === 0 ? (
                <CheckCircle2 className="w-5 h-5 text-success-400 flex-shrink-0 mt-0.5" />
              ) : batchResult.success > 0 ? (
                <AlertCircle className="w-5 h-5 text-amber-450 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-danger-400 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`
                  font-medium
                  ${batchResult.failed === 0 
                    ? 'text-success-400' 
                    : batchResult.success > 0 
                      ? 'text-amber-450'
                      : 'text-danger-400'
                  }
                `}>
                  {batchResult.failed === 0 
                    ? 'All Sales Recorded Successfully!' 
                    : batchResult.success > 0 
                      ? 'Partial Success'
                      : 'All Sales Failed'
                  }
                </p>
                <p className="text-sm text-gray-600 dark:text-warm-sand mt-1">
                  {batchResult.success} succeeded, {batchResult.failed} failed
                </p>
                {batchResult.errors.length > 0 && (
                  <ul className="mt-2 text-xs text-danger-400 space-y-1">
                    {batchResult.errors.slice(0, 3).map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                    {batchResult.errors.length > 3 && (
                      <li>... and {batchResult.errors.length - 3} more errors</li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Location & Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Location <span className="text-danger-400">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
                <select
                  value={locationId}
                  onChange={(e) => { setLocationId(e.target.value); setErrors(p => ({ ...p, locationId: undefined })); }}
                  className={`
                    w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream 
                    focus:outline-none focus:ring-2 focus:ring-green-500/30 transition-all appearance-none
                    ${errors.locationId ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600 focus:border-green-500'}
                  `}
                >
                  <option value="">Select location...</option>
                  {locations?.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              {errors.locationId && (
                <p className="mt-1.5 text-sm text-danger-400">{errors.locationId}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Sale Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
                <input
                  type="date"
                  value={saleDate}
                  onChange={(e) => setSaleDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-all"
                  required
                />
              </div>
            </div>
          </div>

          {/* Commission Info */}
          {locationId && (
            <div className="flex items-center gap-3 p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-450/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-450" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-warm-sand">Commission Rate</p>
                <p className="font-medium text-gray-900 dark:text-warm-cream">{commissionRate}% per sale</p>
              </div>
            </div>
          )}

          {/* Sale Items */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900 dark:text-warm-cream flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-gray-600 dark:text-warm-sand" />
                Sale Items
              </h3>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-charcoal-700 hover:bg-charcoal-600 text-gray-900 dark:text-warm-cream rounded-lg transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => {
                const variants = getProductVariants(item.productId);
                const availableQty = item.variantId ? getAvailableQuantity(item.variantId) : 0;
                const exceedsStock = parseInt(item.quantity) > availableQty;
                const product = products?.find(p => p.id === item.productId);

                return (
                  <div 
                    key={item.id} 
                    className="p-4 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl hover:border-gray-300 dark:border-charcoal-600 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-charcoal-700 flex items-center justify-center text-xs text-gray-600 dark:text-warm-sand font-medium">
                          {index + 1}
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-warm-cream">Item {index + 1}</span>
                        {product && (
                          <span className="text-xs text-gray-600 dark:text-warm-sand">- {product.name}</span>
                        )}
                      </div>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="p-1.5 hover:bg-danger-500/10 text-gray-600 dark:text-warm-sand hover:text-danger-400 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1.5">Product</label>
                        <select
                          value={item.productId}
                          onChange={(e) => updateItem(item.id, 'productId', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:outline-none focus:border-green-500 transition-all text-sm"
                        >
                          <option value="">Select product...</option>
                          {products?.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1.5">
                          Variant
                          {item.variantId && (
                            <span className={`ml-2 ${exceedsStock ? 'text-danger-400' : 'text-success-400'}`}>
                              ({availableQty} avail)
                            </span>
                          )}
                        </label>
                        <select
                          value={item.variantId}
                          onChange={(e) => updateItem(item.id, 'variantId', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:outline-none focus:border-green-500 transition-all text-sm disabled:opacity-50"
                          disabled={!item.productId}
                        >
                          <option value="">Select variant...</option>
                          {variants.map(v => {
                            const variantInv = inventory?.find(i => i.variantId === v.id);
                            const qty = variantInv?.availableQuantity || 0;
                            return (
                              <option key={v.id} value={v.id}>
                                {v.variantSku} ({qty} avail)
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1.5">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          max={availableQty || undefined}
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                          className={`
                            w-full px-3 py-2 bg-gray-100 dark:bg-charcoal-700 border rounded-lg text-gray-900 dark:text-warm-cream 
                            focus:outline-none transition-all text-sm
                            ${exceedsStock ? 'border-danger-500 focus:border-danger-400' : 'border-gray-300 dark:border-charcoal-600 focus:border-green-500'}
                          `}
                        />
                        {exceedsStock && (
                          <p className="mt-1 text-xs text-danger-400">Exceeds stock</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1.5">Sale Price</label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-taupe" />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.salePrice}
                            onChange={(e) => updateItem(item.id, 'salePrice', e.target.value)}
                            className="w-full pl-9 pr-3 py-2 bg-gray-100 dark:bg-charcoal-700 border border-gray-300 dark:border-charcoal-600 rounded-lg text-gray-900 dark:text-warm-cream focus:outline-none focus:border-green-500 transition-all text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Totals */}
          {total > 0 && (
            <div className="p-5 bg-gradient-to-r from-green-500/10 via-charcoal-800 to-amber-450/10 border border-gray-200 dark:border-charcoal-700 rounded-xl mb-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-gray-600 dark:text-warm-sand mb-1">Total Sale</p>
                  <p className="font-display text-2xl font-bold text-gray-900 dark:text-warm-cream">
                    ${total.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-warm-sand mb-1">Commission</p>
                  <p className="font-display text-2xl font-bold text-amber-450">
                    ${commission.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-warm-sand mb-1">You Keep</p>
                  <p className="font-display text-2xl font-bold text-success-400">
                    ${retained.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes about this sale..."
              rows={2}
              className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500 transition-all resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-charcoal-700">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={createSalesMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary min-w-[160px] bg-green-500 hover:bg-green-600 focus:ring-green-500"
              disabled={createSalesMutation.isPending || !locationId}
            >
              {createSalesMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Record Sale
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
